export function useGtm() {
	const config = useAppConfig();
	const trackingConfig = config.google?.tracking;
	const pageViewEnabled = trackingConfig?.events?.includes('pageView') ?? false;
	const {gdprApproved, gdprCookie} = useGdpr();
	const {sha256} = useUtils();
	const currency = useCurrency();
	const info = useInfo();
	const storage = useLocalStorage();
	const route = useRoute();
	const previousRoute = useState('gtmPreviousRoute', () => '');
	const pageViewReadyRoute = useState('gtmPageViewReadyRoute', () => '');
	const eventQueue = useState('gtmEventQueue', () => []);
	const deferredEvents = useState('gtmDeferredEvents', () => []);
	const lastConsent = useState('gtmLastConsent', () => null);
	const auth = useAuth();
	const lang = useLang();
	const webshop = useWebshop();
	const {fetchingCart} = webshop;

	function waitForCart(timeout = 5000) {
		return new Promise((resolve) => {
			if (!fetchingCart.value) return resolve();
			const stop = watch(fetchingCart, (val) => {
				if (!val) {
					stop();
					resolve();
				}
			});
			setTimeout(() => {
				stop();
				resolve();
			}, timeout);
		});
	}

	function getEventName(event) {
		if (trackingConfig?.[event]?.name) return trackingConfig?.[event]?.name; // Check if event name is available in tracking config
		if (event === 'gdprConsents') return 'consent_choice';
		if (event === 'pageView') return 'page_view';
		if (event === 'viewItem') return 'view_item';
		if (event === 'viewItemList') return 'view_item_list';
		if (event === 'selectItem') return 'select_item';
		if (event === 'viewCart') return 'view_cart';
		if (event === 'removeProduct') return 'remove_from_cart';
		if (event === 'addProduct') return 'add_to_cart';
		if (event === 'login') return 'login';
		if (event === 'beginCheckout') return 'begin_checkout';
		if (event === 'addShippingInfo') return 'add_shipping_info';
		if (event === 'addPaymentInfo') return 'add_payment_info';
		if (event === 'purchase') return 'purchase';
		if (event === 'refund') return 'refund';
		if (event === 'addToWishlist') return 'add_to_wishlist';
		if (event === 'viewPromotion') return 'view_promotion';
		if (event === 'selectPromotion') return 'select_promotion';
		return event;
	}

	async function gtmTrack(event, payload, options = {}) {
		if (import.meta.server) return;

		// Exit if event is not defined
		if (!event || (!options?.custom && !trackingConfig?.events?.includes(event))) return;

		// Base event data
		let data = {
			event: getEventName(event),
		};

		// User login data
		if (event == 'login') {
			data.user_id = payload.user_id;
		}

		// Cms Page data
		if (event === 'pageView') {
			if (trackingConfig.pageView?.sendOnLoad === false && previousRoute.value === '') {
				previousRoute.value = route.fullPath;
				pageViewReadyRoute.value = route.fullPath;
				// No pageView on initial load — flush any already-queued events directly
				waitForWindowAndExecute(() => {
					const queued = [...eventQueue.value];
					eventQueue.value = [];
					queued.forEach(item => {
						const plain = JSON.parse(JSON.stringify(item.data));
						if (plain.ecommerce) window.dataLayer.push({ecommerce: null});
						window.dataLayer.push(plain);
						if (trackingConfig?.debug) console.log(item.event, plain);
						if (trackingConfig?.hooks?.afterPush) trackingConfig?.hooks?.afterPush(plain);
					});
				}, 1000, 20000);
				return;
			}
			data.page_title = payload.title;
			data.page_path = payload.url;
		}

		if (['addToWishlist', 'addProduct', 'removeProduct'].includes(event)) {
			const extraData = {...payload};
			delete extraData.items;
			data.ecommerce = {
				currency: currency.getCurrency('code'),
				value: formatTotalPrice(payload),
				items: mapProducts(payload.items, extraData, {useStorage: true}),
			};
		}

		if (['viewItemList'].includes(event)) {
			const extraData = {...payload};
			delete extraData.items;
			data.ecommerce = {};
			if (payload?.item_list_id) data.ecommerce.item_list_id = payload.item_list_id;
			if (payload?.item_list_name) data.ecommerce.item_list_name = payload.item_list_name;
			if (payload?.items) data.ecommerce.items = mapProducts(payload.items, extraData);
		}

		if (['selectItem'].includes(event)) {
			const extraData = {...payload};
			delete extraData.items;
			const selectItemListId = payload?.item_list_id || payload.items?.category_code || '';
			const selectItemListName = payload?.item_list_name || payload.items?.category_title || '';
			data.ecommerce = {
				item_list_id: selectItemListId,
				item_list_name: selectItemListName,
				items: mapProducts(payload.items, {...extraData, item_list_id: selectItemListId, item_list_name: selectItemListName}),
			};

			// Save select item list data to session storage so we can use it in viewItem event
			if (selectItemListId || selectItemListName) {
				storage.create('nuxt:base:gtmSelectItem', {item_list_id: selectItemListId, item_list_name: selectItemListName}, {storage: 'session', replace: true});
			} else {
				storage.remove('nuxt:base:gtmSelectItem', {storage: 'session'});
			}
		}

		if (['viewItem'].includes(event)) {
			const extraData = {...payload};
			delete extraData.items;
			const storageData = storage.get('nuxt:base:gtmSelectItem', {storage: 'session'});
			if (storageData) {
				extraData.item_list_id = storageData.item_list_id;
				extraData.item_list_name = storageData.item_list_name;
			}
			const viewItemProduct = mapProducts(payload.items, extraData);
			data.ecommerce = {
				currency: currency.getCurrency('code'),
				value: formatPrice(payload.items.price),
				items: viewItemProduct,
			};
		}

		if (['viewPromotion', 'selectPromotion'].includes(event)) {
			const extraData = {...payload};
			delete extraData.items;
			data.ecommerce = {};
			if (payload.promotions?.length) {
				// If promotions array is provided, use this. Fields needs to be provided manually
				data.ecommerce.items = payload.promotions;
			} else {
				if ('promotion_id' in payload) data.ecommerce.promotion_id = payload.promotion_id;
				if ('promotion_name' in payload) data.ecommerce.promotion_name = payload.promotion_name;
				if ('creative_name' in payload) data.ecommerce.creative_name = payload.creative_name;
				if ('creative_slot' in payload) data.ecommerce.creative_slot = payload.creative_slot;
				if ('location_id' in payload) data.ecommerce.location_id = payload.location_id;
				if (payload?.items) data.ecommerce.items = mapProducts(payload.items, extraData);
			}
		}

		if (['viewCart', 'beginCheckout', 'addShippingInfo', 'addPaymentInfo', 'purchase', 'refund'].includes(event)) {
			const cartData = payload.cart;
			const cartItems = getCartProducts(cartData);
			if (!cartItems?.length) return;
			data.ecommerce = {};
			if (cartData) {
				data.ecommerce.currency = currency.getCurrency('code');
				data.ecommerce.value = formatPrice(cartData.total.total_items_basic);
			}
			const extraData = {};

			// Add coupon data
			if (['beginCheckout', 'addShippingInfo', 'addPaymentInfo', 'refund', 'purchase'].includes(event)) {
				const coupon = cartData.total?.extraitems?.find(el => el.type === 'coupon');
				const couponCode = coupon?.meta_data?.find(meta => meta.applied)?.code || coupon?.code || '';
				if (couponCode) {
					data.ecommerce.coupon = couponCode;
					extraData.coupon = couponCode;
				}
			}

			// Add shipping data
			if (event == 'addShippingInfo') {
				data.ecommerce.shipping_tier = payload.shipping_tier || '';
			}

			// Add payment data
			if (event == 'addPaymentInfo') {
				data.ecommerce.payment_type = payload.payment_type || '';
			}

			// Purchase data
			if (['purchase'].includes(event)) {
				data.ecommerce.affiliation = trackingConfig.affiliation || removeProtocol(info.getInfo('site_url')) || '';
				data.ecommerce.shipping = formatPrice(cartData.total?.extra_items?.find(el => el.type === 'shipping')?.base || 0);
				data.ecommerce.tax = formatPrice(cartData.total.total_tax);
				data.ecommerce.transaction_id = payload.transaction_id || '';
				const gclid = storage.get('nuxt:base:gclid', {raw: true});
				if (gclid) data.ecommerce.gclid = gclid;
			}

			if (event == 'refund') {
				data.ecommerce.affiliation = trackingConfig.affiliation || removeProtocol(info.getInfo('site_url')) || '';
				data.ecommerce.shipping = formatPrice(cartData.total?.extra_items?.find(el => el.type === 'shipping')?.base || 0);
				data.ecommerce.tax = formatPrice(cartData.total.total_tax);
				data.ecommerce.value = formatPrice(cartData.total?.total_basic || 0);
				data.ecommerce.transaction_id = payload.transaction_id || '';
			}

			data.ecommerce.items = mapProducts(cartItems, extraData, {useStorage: true});
		}

		// Map data if mapData hook is defined
		if (trackingConfig[event]?.hooks?.mapData) {
			if (event === 'pageView') await waitForCart();
			const mappedData = await trackingConfig[event].hooks.mapData(data, {purchaseData: payload, sha256, referrer: previousRoute.value, user: auth.getUser(), cart: webshop.getCartData(), currentLang: lang.get(), route});
			data = {event: data.event, ...mappedData};
		}

		// Custom event data
		if (options?.custom) {
			data = {...data, ...payload};
		}

		// Queue non-pageView events until pageView fires for this route
		if (pageViewEnabled && event !== 'pageView' && pageViewReadyRoute.value !== route.fullPath) {
			eventQueue.value.push({event, data});
			return;
		}

		if (event === 'pageView') {
			pushToDataLayer(event, data, {
				onPushed: pageViewPushed,
			});
		} else {
			pushToDataLayer(event, data);
		}
	}

	function getCartProducts(cart) {
		let cartItems = [];
		if (cart.parcels?.length) {
			cart.parcels.forEach(parcel => {
				cartItems.push(...parcel.items);
			});
		}
		return cartItems;
	}

	function mapProducts(products, extraData = {}, options = {}) {
		if (!products) {
			useLog('Products not provided', 'error');
			return [];
		}

		// Convert single product to array
		if (!Array.isArray(products)) products = [products];

		return products.map((product, index) => {
			let price = 0;
			let discount = 0;
			let variant = '';
			let item_list_id = product.item_list_id || product.category_code || '';
			let item_list_name = product.item_list_name || product.category_title || '';

			const categories = {};
			if (product.category_parents?.length) {
				product.category_parents.forEach((category, index) => {
					categories[`item_category${index + 2}`] = category.title;
				});
			}

			// If category_hierarchy_parents exists, use it instead of category_parents
			if (product.category_hierarchy_parents?.length) {
				product.category_hierarchy_parents.forEach((category, index) => {
					const key = index === 0 ? 'item_category' : `item_category${index + 1}`;
					categories[key] = category.title;
				});
			}

			// Regular product data
			if (product.discount_percent_custom && product.basic_price_custom) {
				discount = product.basic_price_custom - product.price_custom;
			}
			if (product.price_custom) price = product.price_custom;
			if (product.variation?.attributes) variant = product.variation.attributes;

			// Cart data
			if (product.item) {
				if (product.discount_amount) discount = product.discount_amount;
				if (product.unit_price) price = product.unit_price;
				if (product.item?.variation?.attributes) variant = product.item.variation.attributes;
				if (product.item?.category?.parents) {
					product.item.category.parents.forEach((category, index) => {
						const key = index === 0 ? 'item_category' : `item_category${index + 1}`;
						categories[key] = category.title;
					});
				}
			}

			// Get item list id and name from localStorage
			if (options?.useStorage) {
				const gtmCartItem = storage.get('nuxt:base:gtmCartItems', {condition: cartItem => cartItem.shopping_cart_code == product.shopping_cart_code, single: true});
				if (gtmCartItem) {
					item_list_id = gtmCartItem.item_list_id;
					item_list_name = gtmCartItem.item_list_name;
				}
			}

			const itemVariant = stringifyAttributes(variant);
			const productData = {
				affiliation: trackingConfig.affiliation || removeProtocol(info.getInfo('site_url')) || '',
				currency: currency.getCurrency('code'),
				discount: formatPrice(discount),
				item_id: product.item?.code || product.code || '',
				item_name: product.item?.title || product.title || '',
				index: index,
				item_brand: product.item?.manufacturer?.title || product.manufacturer_title || '',
				item_list_name,
				item_list_id,
				item_category: product.item?.category?.title || product.category_title || '',
				...(itemVariant ? {item_variant: itemVariant} : {}), // Only add if variant exists
				price: formatPrice(price),
				quantity: product.quantity || 1,
				...categories,
				...extraData,
			};

			// Configure specific product values
			if (trackingConfig?.productItemIdValue == 'id') productData.item_id = product.item?.id || product.id || '';
			if (trackingConfig?.productItemCodeValue == 'code') productData.item_code = product.item?.code || product.code || '';

			// Custom product mapping hook if defined
			if (trackingConfig?.hooks?.mapProduct) {
				return trackingConfig?.hooks?.mapProduct(product, productData);
			}

			return productData;
		});
	}

	function removeProtocol(url) {
		return url.replace(/^https?:\/\//, '');
	}

	function formatTotalPrice(payload) {
		let price = 0;
		let quantity = 1;

		if (Array.isArray(payload.items)) {
			return payload.items.reduce((total, item) => {
				price = item.price_custom || item.unit_price;
				quantity = payload.quantity || item.quantity || 1;
				return formatPrice(total + price * quantity);
			}, 0);
		}

		price = payload.items.price_custom || payload.items.unit_price;
		quantity = payload.quantity || payload.items.quantity || 1;
		return formatPrice(price * quantity);
	}

	function formatPrice(price) {
		let p = parseFloat(price).toFixed(2);
		return parseFloat(p);
	}

	function stringifyAttributes(attributes) {
		if (!attributes) return '';
		return Object.values(attributes)
			.map(attr => {
				return `${attr.attribute_title}:${attr.title}`;
			})
			.join(',');
	}

	function gtmTrackGdpr(cookie) {
		if (import.meta.server || !trackingConfig?.events?.includes('gdprConsents')) return;

		const consentCookie = cookie || null;
		if (lastConsent.value?.cookie === consentCookie) return;

		const eventName = getEventName('gdprConsents');
		const gdpr = consentCookie?.split('|') || [];
		const analytics = gdpr.includes('analytics') ? 'granted' : 'denied';
		const marketing = gdpr.includes('marketing') ? 'granted' : 'denied';
		const data = {
			'event': eventName,
			'analytics': analytics,
			'marketing': marketing,
			'personalization': marketing,
			'ad_storage': marketing,
			'analytics_storage': analytics,
			'ad_user_data': marketing,
			'ad_personalization': marketing,
		};

		// Consent must be queued before tracking, even while the GTM script is loading.
		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push(data);
		lastConsent.value = {cookie: consentCookie};
		if (trackingConfig?.debug) console.log(eventName, data);
		if (consentCookie && trackingConfig?.hooks?.afterPush) trackingConfig.hooks.afterPush(data);
	}

	function deferEvent(event, data, options = {}) {
		// Events from a page that was left before consent must not be attributed to the new route.
		if (deferredEvents.value.some(item => item.route !== route.fullPath)) {
			deferredEvents.value = [];
			eventQueue.value = [];
		}
		deferredEvents.value.push({
			event,
			route: route.fullPath,
			gdpr: options.gdpr || trackingConfig?.gdpr,
			data: JSON.parse(JSON.stringify(data)),
		});
	}

	function pageViewPushed() {
		previousRoute.value = route.fullPath;
		pageViewReadyRoute.value = route.fullPath;
		const queued = [...eventQueue.value];
		eventQueue.value = [];
		queued.forEach(item => {
			const plain = JSON.parse(JSON.stringify(item.data));
			if (plain.ecommerce) window.dataLayer.push({ecommerce: null});
			window.dataLayer.push(plain);
			if (trackingConfig?.debug) console.log(item.event, plain);
			if (trackingConfig?.hooks?.afterPush) trackingConfig?.hooks?.afterPush(plain);
		});
	}

	function activateDeferredEvents() {
		if (import.meta.server || !deferredEvents.value.length) return;

		const deferred = deferredEvents.value;
		deferredEvents.value = [];
		const currentRouteEvents = deferred.filter(item => item.route === route.fullPath);
		if (currentRouteEvents.length !== deferred.length) eventQueue.value = [];
		currentRouteEvents.forEach(item => {
			if (item.gdpr && !gdprApproved(item.gdpr)) {
				deferredEvents.value.push(item);
				return;
			}
			pushToDataLayer(item.event, item.data, item.event === 'pageView' ? {onPushed: pageViewPushed} : {});
		});
	}

	// Wait for window to load
	function waitForWindowAndExecute(action, interval = 1000, timeout = 20000) {
		let elapsedTime = 0;

		function checkWindow() {
			// In development, skip GTM checks and only verify window and dataLayer
			if (process.env.NODE_ENV == 'development') {
				if (typeof window !== 'undefined' && typeof window?.dataLayer !== 'undefined') {
					gtmTrackGdpr(gdprCookie());
					action();
				} else if (elapsedTime < timeout) {
					elapsedTime += interval;
					setTimeout(checkWindow, interval);
				}
			} else {
				// In production, perform full GTM checks
				if (typeof window !== 'undefined' && typeof window?.dataLayer !== 'undefined' && window?.google_tag_manager?.dataLayer?.gtmDom === true && window?.google_tag_manager?.dataLayer?.gtmLoad === true) {
					gtmTrackGdpr(gdprCookie());
					action();
				} else if (elapsedTime < timeout) {
					elapsedTime += interval;
					setTimeout(checkWindow, interval);
				}
			}
		}

		checkWindow();
	}

	// Push data to GTM dataLayer
	function pushToDataLayer(event, data, options = {}) {
		if (import.meta.server) return;

		// For custom event, check if gdpr is approved (if defined). For other events, use tracking config
		if (options?.gdpr) {
			if (!gdprApproved(options?.gdpr)) {
				if (!options?.ignoreGdpr) deferEvent(event, data, options);
				return;
			}
		} else {
			if (trackingConfig.gdpr && !gdprApproved(trackingConfig.gdpr) && !options?.ignoreGdpr) {
				deferEvent(event, data, options);
				return;
			}
		}

		// Do not push if items exists but is empty
		if (data.ecommerce && 'items' in data.ecommerce && !data.ecommerce.items?.length) {
			return;
		}

		waitForWindowAndExecute(
			() => {
				if (window?.dataLayer) {
					const plain = JSON.parse(JSON.stringify(data));
					if (plain.ecommerce) dataLayer.push({ecommerce: null});
					dataLayer.push(plain);
					if (trackingConfig?.debug) console.log(event, plain);
					if (trackingConfig?.hooks?.afterPush) trackingConfig?.hooks?.afterPush(plain);
					if (options?.onPushed) options.onPushed();
				}
			},
			1000,
			20000,
		); // Interval of 1000ms and timeout of 20000ms (20 seconds)
	}

	return {activateDeferredEvents, gtmTrackGdpr, gtmTrack};
}
